import './SitKpiHeader.scss';
import React, {useLayoutEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {Button, Popover, Tooltip, useMediaQuery, type Theme} from "@mui/material";
import {KpiRow} from "~/components/KpiRow/KpiRow";
import {KPI_GAP, pillsThatFit} from "~/components/KpiRow/kpiRowLayout";
import i18n from "../../../i18n/i18n";

/**
 * KPI header pills — ported from the Mithra 3.0 design (`.m3-kpi`): an outline
 * pill with a coloured icon, a muted label and a bold value. Replaces the
 * generic KpiBoxedValues on the Savings Initiative Tracker and is reused by
 * the Contract Explorer and Payment Terms modules. Pills that do not fit on one line go behind "Show more".
 */
export type SitKpiPill = {
    icon: React.ReactNode;
    tone: 'ok' | 'info' | 'warn' | 'plain';
    label: React.ReactNode;
    value: React.ReactNode;
    /** Optional explanation, shown as a tooltip on hover. */
    help?: React.ReactNode;
};

/** Pills shown on phones (below 600px) when `primary` is set. */
const PHONE_PRIMARY = 2;

type Props = {
    items: SitKpiPill[];
    /**
     * Opt-in: show the first `primary` pills (2 on phones; fewer when they do not fit on one line) and list every
     * metric behind "All metrics".
     * Without it, the pills that fit are shown and the rest go behind "Show more".
     */
    primary?: number;
};

export const SitKpiHeader: React.FC<Props> = observer(({items, primary}) => {
    const phone = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const row = useRef<HTMLDivElement>(null);
    const button = useRef<HTMLButtonElement>(null);
    const pills = useRef<(HTMLElement | null)[]>([]);
    const widths = useRef<number[]>([]);
    const [fit, setFit] = useState(primary ?? items.length);
    const wanted = primary === undefined ? 0 : Math.min(primary, phone ? PHONE_PRIMARY : primary, items.length);

    // Split mode stays on one line: of the key pills, show those that fit next to "All metrics".
    useLayoutEffect(() => {
        const el = row.current;
        if (!el || !wanted) return undefined;
        const measure = () => {
            // Keep the widest each pill has been: hidden pills report 0, and a squeezed first pill reports less
            // than it needs.
            const known: number[] = [];
            for (let i = 0; i < wanted; i++) {
                const w = pills.current[i]?.offsetWidth ?? 0;
                widths.current[i] = Math.max(widths.current[i] ?? 0, w);
                known.push(widths.current[i] ?? 0);
            }
            const btn = button.current?.offsetWidth ?? 0;
            setFit(pillsThatFit(known, el.clientWidth - (btn ? btn + KPI_GAP : 0), 0));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [wanted, items]);
    const pill = (k: SitKpiPill, i: number) => {
        const p = (
            <span key={i} className={'sit-kpi-pill tone-' + k.tone}>
                {k.icon}
                <span className="sit-kpi-label">{k.label}:</span>
                <b>{k.value}</b>
            </span>
        );
        return k.help
            ? <Tooltip key={i} title={k.help} placement="bottom" arrow enterTouchDelay={0}>{p}</Tooltip>
            : p;
    };

    if (primary === undefined) {
        return <div className="sit-kpis"><KpiRow>{items.map(pill)}</KpiRow></div>;
    }
    const shown = Math.min(wanted, fit);
    return (
        <div className={'sit-kpis sit-kpis-split' + (shown === 1 ? ' is-squeezed' : '')} ref={row}>
            {items.slice(0, wanted).map((k, i) => (
                <span key={i} className="sit-kpi-slot" ref={n => { pills.current[i] = n; }}
                      style={i >= shown ? {display: 'none'} : undefined}>
                    {pill(k, i)}
                </span>
            ))}
            {items.length > shown && <>
                <Button ref={button} size="small" className="sit-kpi-all-btn" aria-haspopup="dialog" aria-expanded={Boolean(anchor)}
                        onClick={e => setAnchor(e.currentTarget)}>
                    {i18n.t('topKpi.allMetrics', {ns: 'sit'})}
                </Button>
                <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                         anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}>
                    <ul className="sit-kpi-all" aria-label={i18n.t('topKpi.allMetrics', {ns: 'sit'})}>
                        {items.map((k, i) => (
                            <li key={i} className={'tone-' + k.tone}>
                                {k.icon}
                                <span className="sit-kpi-label">{k.label}</span>
                                <b>{k.value}</b>
                            </li>
                        ))}
                    </ul>
                </Popover>
            </>}
        </div>
    );
});
